import * as Sentry from "@sentry/react";
import { Component, type ReactNode } from "react";

type Props = { children: ReactNode; onError?: (error: Error) => void };
type State = { failed: boolean };

/**
 * A malformed package or a renderer fault inside the vendor editor must not
 * take down the Case IDE or its file controls. The boundary shows a short
 * message in the editor's place; the rest of the pane keeps working.
 */
export class DocxEditorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    const { onError } = this.props;
    Sentry.captureException(error);
    onError?.(error);
  }

  render() {
    const { failed } = this.state;
    const { children } = this.props;
    if (failed) {
      return (
        <div
          role="alert"
          className="flex h-full w-full items-center justify-center px-6 text-center"
        >
          <span className="text-sm text-muted-foreground">
            The document editor hit a problem rendering this draft. Reload the
            tab to try again, or download the .docx to open it in Word.
          </span>
        </div>
      );
    }
    return children;
  }
}

export default DocxEditorBoundary;
